// One line of the transcript.
//
// ---------------------------------------------------------------------------
// A SYSTEM MESSAGE IS RENDERED FROM ITS METADATA AND NEVER FROM ITS BODY
// ---------------------------------------------------------------------------
//
// "System messages ('Seth joined') render from their metadata, not from stored prose, so
// they can be re-rendered later." `message.body` for a system message is prose somebody
// wrote once; `metadata.subject` is a roster reference the API validated against this
// plan's roster. Only the second is read here. That is why a person who changes how their
// name is written sees every "joined" line change with it, and why translating this
// interface later is a change to this file rather than a data migration.
//
// The body of a system message is in scope on the line below and is not read. That is
// deliberate: a version that read it would render whatever prose the server happened to
// store rather than the sentence this file composes.
//
// ---------------------------------------------------------------------------
// WHO SPOKE
// ---------------------------------------------------------------------------
//
// The speaker is `message.type` and, for a human, the name of `authorId` in the people
// list. There is no role anywhere: nobody is a principal here, nobody is an interested
// party, and there is no value in this component that could be turned into either — the
// person decoder drops the field before it becomes a value. See `api/contract.ts`.
//
// ---------------------------------------------------------------------------
// AND WHICH OF THEM IS YOU
// ---------------------------------------------------------------------------
//
// Every line in a family's transcript used to be a name, including yours, which left a
// reader working out which lines were theirs from the content — in the one interface where
// being wrong about who said something is worst. So a message whose author is the person
// reading says "You" and carries `message--mine`.
//
// THE ANSWER COMES OUT OF `people`, THE SAME ARRAY THE NAME COMES OUT OF, in the same
// lookup: `personFor` returns the roster entry and the caller asks it for a name or for
// `self`. Nothing is stored, nothing is compared against a session, and nothing here knows
// what an account is — so this component cannot be out of date about who is reading it,
// because the snapshot it is rendering carries the answer with the question.
//
// The word and the class are BOTH load-bearing. A class alone is a colour, and colour alone
// is not a distinction everyone can see — the same reason the facilitator's panel is a panel
// and not a tint. "You" is the plainest thing the interface can say, and it is the reader's
// own view: the record on the server is identical for everyone, and the only thing that
// differs is which line this reader is being shown as theirs.
//
// ---------------------------------------------------------------------------
// WHAT IS NOT HERE
// ---------------------------------------------------------------------------
//
// No edit, no delete, no reaction, no "seen by", no relative-time badge that ticks, and no
// READ RECEIPT — nothing here tells one participant what another has read. A message is a
// thing that was said. The only affordance on it is reading it, and the one thing this
// client tracks about that is self-facing: which threads YOU have unread, on the tab nav in
// `PlanScreen.tsx`, never who else has seen this line.

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  GRANT_PRINCIPAL_ACTION,
  INVITATION_ACTION,
  INVITE_ACTION,
  MOVE_ACTION,
  TOPIC_ACTION,
  type Decision,
  type IssueEntryRef,
  type Message,
  type MessageChoice,
  type Person,
  type RecordChangeLine,
  type TopicView,
} from '../api/contract.js';
import { nameList, topicPhrase } from '../format.js';
import { entryKey } from '../plan/issue.js';

export type MessageRowProps = {
  message: Message;
  people: readonly Person[];
  /** The plan the message is in, for links to its files. */
  planId: string;
  /** Show the author's name above other people's messages: true in any conversation with
   * more than one person in it. */
  showAuthor?: boolean;
  /** Arrived after the conversation first rendered: fades in once. */
  isNew?: boolean;
  /**
   * In an issue conversation: the turn each entry's current version was written under, so a
   * line offers Undo only while its card is still the latest word on that entry.
   */
  issue?: {
    readonly turns: ReadonlyMap<string, string>;
    /** The newest card or divider in the thread: a dismissal or cleared field on it offers Undo. */
    readonly newest: string | null;
    readonly undo: (entry: IssueEntryRef) => void;
    readonly busy: boolean;
  };
  /**
   * What an added-issue card line needs: the issues and topics as the reader sees them, the
   * conversation the card sits in (for the Back link), and how Undo acts.
   */
  added?: {
    readonly decisions: readonly Decision[];
    readonly topics: readonly TopicView[];
    readonly thread: {
      readonly topic: string | null;
      readonly issue: string | null;
    } | null;
    readonly isPrincipal: boolean;
    readonly busy: boolean;
    readonly remove: (decision: string) => void;
  };
  /** Present only on the newest message in a conversation: its offered replies become buttons. */
  choices?: {
    readonly choose: (choice: MessageChoice) => void;
    readonly busy: boolean;
    /** What a choice's button says; null hides the choice. Absent, the button shows its text. */
    readonly label?: (choice: MessageChoice) => string | null;
  };
};

/** The facilitator's name in the interface. It speaks as a participant, not as a feature. */
export const FACILITATOR_NAME = 'Mor';

export function MessageRow({
  message,
  people,
  planId,
  showAuthor = false,
  isNew = false,
  issue,
  added,
  choices,
}: MessageRowProps): JSX.Element | null {
  const location = useLocation();
  const nw = isNew ? ' is-new' : '';
  if (message.type === 'system' && message.metadata.systemKind === 'record_card') {
    const lines = message.metadata.changes;
    if (lines.length === 0) {
      return null;
    }
    const isMixed = !allChangesSame(lines);
    const author = personFor(message.metadata.subject, people);
    const undoOf = (line: RecordChangeLine): JSX.Element | null =>
      undoButton(line, message.id, issue);
    const heading =
      author === null || author.self
        ? cardHeading(lines)
        : authoredCardHeading(author.name, lines);
    return (
      <li className={`message message--system${nw}`}>
        <div className="recordCard">
          <p className="recordCard__heading">{heading}</p>
          <ul className="recordCard__lines">
            {[
              ...lines.filter((line) => line.item !== 'person'),
              ...lines.filter((line) => line.item === 'person'),
            ].map((line, index) => (
              <li key={index}>
                {isAddedIssueLine(line) && added !== undefined
                  ? addedIssueLine(line, added, planId, location.pathname)
                  : cardLine(line, isMixed)}
                {undoOf(line)}
              </li>
            ))}
          </ul>
        </div>
      </li>
    );
  }
  if (message.type === 'system' && message.metadata.systemKind === 'record_edit') {
    const line = message.metadata.changes[0];
    if (line === undefined) {
      return null;
    }
    const actor = personFor(message.metadata.subject, people);
    const who = actor === null || actor.self === true ? 'You' : actor.name;
    return (
      <li className={`message message--system${nw}`}>
        <p className="recordDivider">
          <span>{editSentence(who, line)}</span>
          {undoButton(line, message.id, issue)}
        </p>
      </li>
    );
  }
  if (message.type === 'system' && message.metadata.systemKind === 'opened_from') {
    if (message.metadata.from === 'home' || message.metadata.from === 'conversation') {
      const said = message.metadata.said;
      return (
        <>
          <li className={`message message--system${nw}`}>
            <p className="recordDivider">
              <span>
                {message.metadata.from === 'home'
                  ? 'From the home page'
                  : `Continued from ${message.metadata.fromTitle ?? 'your conversation'}`}
              </span>
            </p>
          </li>
          {said === null || said === '' ? null : (
            <li className={classFor(false, true) + nw}>
              <div className="message__content">
                <div className="message__paragraphs">
                  {said.split(/\n{2,}/u).map((paragraph, index) => (
                    <p key={index} className="message__body">
                      {bodyParts(paragraph, planId)}
                    </p>
                  ))}
                </div>
              </div>
            </li>
          )}
        </>
      );
    }
    return (
      <li className={`message message--system${nw}`}>
        <p className="recordDivider">
          <span>Opened from your first conversation</span>
        </p>
      </li>
    );
  }
  if (message.type === 'system' && message.metadata.systemKind === 'opened_suggestion') {
    return (
      <li className={`message message--system${nw}`}>
        <p className="recordDivider">
          <span>You opened {message.metadata.title ?? ''}</span>
        </p>
      </li>
    );
  }
  if (message.type === 'system' && message.metadata.systemKind === 'file_added') {
    const { fileId, fileName } = message.metadata;
    const name = fileName ?? 'a file';
    return (
      <li className={`message message--system${nw}`}>
        <p className="message__systemText">
          You added{' '}
          {fileId === null ? (
            name
          ) : (
            <Link to={`/plans/${planId}/files/${fileId}`}>{name}</Link>
          )}
        </p>
      </li>
    );
  }
  if (message.type === 'system' && message.metadata.systemKind === 'file_ready') {
    const { fileId, fileName } = message.metadata;
    const name = fileName ?? 'A file';
    return (
      <li className={`message message--system${nw}`}>
        <p className="message__systemText">
          {fileId === null ? (
            name
          ) : (
            <Link to={`/plans/${planId}/files/${fileId}`}>{name}</Link>
          )}{' '}
          was added to the plan
        </p>
      </li>
    );
  }
  if (message.type === 'system') {
    return (
      <li className={`message message--system${nw}`}>
        <p className="message__systemText">{systemSentence(message, people)}</p>
      </li>
    );
  }

  const facilitator = message.type === 'facilitator';
  const author = facilitator ? null : personFor(message.authorId, people);
  const mine = author?.self === true;
  // Never the facilitator: Mor is named as itself and has no seat on the roster, so a mark
  // saying "a model wrote this" would be telling the reader something they can already see.
  const character = author?.character === true;
  const speaker = facilitator
    ? FACILITATOR_NAME
    : author === null
      ? 'Someone'
      : shortName(author, people);
  const addressed = facilitator ? addressee(message.metadata.addressed, people) : null;

  return (
    <li className={classFor(facilitator, mine) + nw}>
      <div className="message__content">
        {!mine && showAuthor ? (
          <p className="message__attribution">
            {character ? (
              // Which of the names answering you is a model. Only a demo has any.
              // `aria-label` because "AI" read aloud one letter at a time is noise.
              <span className="message__character" aria-label="Played by AI">
                AI
              </span>
            ) : null}
            <span className="message__speaker">{speaker}</span>
          </p>
        ) : null}
        {addressed === null ? null : (
          // What makes an ask legible to the room: which of them was asked.
          <p className="message__addressed">To {addressed}</p>
        )}
        <div className="message__paragraphs">
          {message.body.split(/\n{2,}/u).map((paragraph, index) => (
            <p key={index} className="message__body">
              {bodyParts(paragraph, planId)}
            </p>
          ))}
        </div>
        {facilitator && choices !== undefined && message.metadata.choices.length > 0
          ? (() => {
              const button = (
                choice: (typeof message.metadata.choices)[number],
                index: number,
              ) => {
                const text =
                  choices.label === undefined ? choice.text : choices.label(choice);
                return text === null ? null : (
                  <button
                    key={index}
                    type="button"
                    className="button button--outline message__choice"
                    disabled={choices.busy}
                    onClick={() => choices.choose(choice)}
                  >
                    {text}
                  </button>
                );
              };
              const indexed = message.metadata.choices.map((choice, index) => ({
                choice,
                index,
              }));
              const begin = indexed.filter(
                ({ choice }) => choice.suggestion !== undefined,
              );
              if (begin.length === 0) {
                return (
                  <div className="message__choices" role="group" aria-label="Replies">
                    {indexed.map(({ choice, index }) => button(choice, index))}
                  </div>
                );
              }
              const others = indexed.filter(
                ({ choice }) => choice.suggestion === undefined,
              );
              return (
                <>
                  <div
                    className="message__begin"
                    role="group"
                    aria-label="Where you could begin"
                  >
                    <p className="message__beginHeading">Where you could begin</p>
                    <p className="message__beginNote">
                      Pick one to work through it with Mor.
                    </p>
                    <div className="message__choices">
                      {begin.map(({ choice, index }) => button(choice, index))}
                    </div>
                  </div>
                  {others.length > 0 ? (
                    <div className="message__choices" role="group" aria-label="Replies">
                      {others.map(({ choice, index }) => button(choice, index))}
                    </div>
                  ) : null}
                </>
              );
            })()
          : null}
      </div>
    </li>
  );
}

export const FILE_MARKER = /\[\[file:([^|\]]*)\|([^\]]*)\]\]/g;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A body as text and links: `[[file:<uuid>|words]]` becomes a link showing `words`. */
function bodyParts(body: string, planId: string): ReactNode {
  if (!body.includes('[[file:')) {
    return body;
  }
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of body.matchAll(FILE_MARKER)) {
    const [whole, id = '', words = ''] = match;
    const at = match.index ?? 0;
    parts.push(body.slice(last, at));
    parts.push(
      UUID.test(id) ? (
        <Link key={at} to={`/plans/${planId}/files/${id}`}>
          {words}
        </Link>
      ) : (
        words
      ),
    );
    last = at + whole.length;
  }
  parts.push(body.slice(last));
  return parts;
}

/** First name, plus the last initial when another person on the list shares the first name,
 *  and the full name when that still matches someone else. */
function shortName(person: Person, people: readonly Person[]): string {
  const label = (who: Person): string => {
    const [first = who.name, ...rest] = who.name.trim().split(/\s+/);
    const clash = people.some(
      (other) => other.id !== who.id && other.name.trim().split(/\s+/)[0] === first,
    );
    const initial = rest[rest.length - 1]?.[0];
    return clash && initial !== undefined ? `${first} ${initial}.` : first;
  };
  const own = label(person);
  return people.some((other) => other.id !== person.id && label(other) === own)
    ? person.name.trim()
    : own;
}

function classFor(facilitator: boolean, mine: boolean): string {
  if (facilitator) {
    return 'message message--facilitator';
  }
  return mine ? 'message message--person message--mine' : 'message message--person';
}

/**
 * The roster entry a message names, or null.
 *
 * One lookup, returning the whole person, so that "what are they called" and "is this me"
 * are answered from one row of one array rather than from two places that could disagree.
 */
function personFor(
  rosterEntryId: string | null,
  people: readonly Person[],
): Person | null {
  if (rosterEntryId === null) {
    return null;
  }
  return people.find((person) => person.id === rosterEntryId) ?? null;
}

/** Who the facilitator spoke to, in the second person when it is the person reading. */
function addressee(
  rosterEntryId: string | null,
  people: readonly Person[],
): string | null {
  const person = personFor(rosterEntryId, people);
  if (person === null) {
    return null;
  }
  return person.self ? 'you' : person.name;
}

/**
 * The four system occurrences the metadata can describe: somebody joining, an
 * invitation being approved, a confirmation being answered, or the terminal speaking
 * notice.
 *
 * `systemKind` is the whole of how this file tells them apart. Every roster reference the
 * first three carry — `subject`, `target`, `notWrittenTo` — is one the server has already
 * checked against this plan's roster, and one the roster does not contain is dropped
 * before it gets here; the fallbacks below are for that case, and say the true thing
 * without naming anybody. The speaking notice carries none of them: its sentence names
 * nobody and reports no outcome, so it needs nothing out of `metadata` at all.
 */
function systemSentence(message: Message, people: readonly Person[]): string {
  if (message.metadata.systemKind === 'speaking_notice') {
    // The terminal notice: the group loop's speaking fallback and the 1:1 loop's dropped
    // bundle both raise this same system entry, never a facilitator message — see
    // `docs/decisions.md`. Composed here, from the kind alone, and never from
    // `message.body`: the wording lives in exactly one place.
    return 'Something went wrong. Nothing anyone said has been lost.';
  }
  if (message.metadata.systemKind === 'confirmation_resolved') {
    const actor = personFor(message.metadata.subject, people);
    const who = actor?.self === true ? 'You' : (actor?.name ?? 'Someone');
    const verb = message.metadata.outcome === 'ratified' ? 'approved' : 'declined';
    const target = personFor(message.metadata.target, people)?.name ?? 'them';
    const said = (() => {
      switch (message.metadata.actionType) {
        case MOVE_ACTION:
          return `${who} ${verb} inviting the family into this topic`;
        case INVITE_ACTION:
          return `${who} ${verb} inviting ${target} into this topic`;
        case INVITATION_ACTION:
          return `${who} ${verb} inviting ${target}.`;
        case GRANT_PRINCIPAL_ACTION:
          return message.metadata.outcome === 'ratified'
            ? `${who} added ${target} as a principal.`
            : `${who} chose not to add ${target} as a principal yet.`;
        case TOPIC_ACTION: {
          const topic = message.metadata.title ?? '';
          if (message.metadata.outcome !== 'ratified') {
            return `${who} declined the suggested topic “${topic}”.`;
          }
          return message.metadata.alreadyListed
            ? `${who} accepted the suggested topic “${topic}”, which was already on the list.`
            : `${who} added the suggested topic “${topic}”.`;
        }
        default:
          return `${who} ${verb} the confirmation.`;
      }
    })();
    // A grant writes to nobody (D121); older grant notices may still carry the list.
    const excluded =
      message.metadata.actionType === GRANT_PRINCIPAL_ACTION
        ? null
        : notWrittenToSentence(message.metadata.notWrittenTo, people);
    return excluded === null
      ? said
      : `${said}${said.endsWith('.') ? '' : '.'} ${excluded}`;
  }
  if (message.metadata.systemKind === 'confirmation_noted') {
    // A principal's confirmation of the move that did not yet complete it.
    const actor = personFor(message.metadata.subject, people);
    const who = actor?.self === true ? 'You' : (actor?.name ?? 'Someone');
    return `${who} confirmed inviting the family.`;
  }
  if (message.metadata.systemKind === 'confirmation_withdrawn') {
    return 'The request to invite the family was withdrawn: it needs every principal to confirm together.';
  }
  if (message.metadata.systemKind === 'invited') {
    // D105: posted into every conversation the invited person will be seated in once
    // they join, so nobody already in it is surprised later by who can read it.
    const approver = personFor(message.metadata.subject, people);
    const who = approver?.self === true ? 'You' : (approver?.name ?? 'Someone');
    const target = personFor(message.metadata.target, people)?.name ?? 'someone';
    return `${who} invited ${target} to join this conversation.`;
  }
  return joinedSentence(message.metadata.subject, people);
}

/**
 * Who a ratification did not write to (issue #155, D96), as the permanent line that
 * survives in the thread — the resolve response says the same thing once, at the
 * moment of approving, and this is what is left once that moment has passed. Names come
 * from `people`, the same list every other name on this row comes from, never from the
 * metadata itself. Null when the metadata carries nobody, which is every message this
 * did not happen on and every decline.
 */
function notWrittenToSentence(
  rosterEntryIds: readonly string[],
  people: readonly Person[],
): string | null {
  if (rosterEntryIds.length === 0) {
    return null;
  }
  const names = rosterEntryIds.map((id) => personFor(id, people)?.name ?? 'someone');
  // No actor, for the same reason the confirmation's note has none: say what did not
  // reach whom, rather than inventing someone who did not do it.
  return `This didn't reach ${nameList(names)}. There's no address for them yet.`;
}

function joinedSentence(subject: string | null, people: readonly Person[]): string {
  const person = personFor(subject, people);
  if (person === null) {
    return 'Someone new joined the conversation.';
  }
  return person.self
    ? 'You joined the conversation.'
    : `${person.name} joined the conversation.`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Relationship lines describe people on the list; they are not people. */
function isRelationshipLine(line: RecordChangeLine): boolean {
  return line.change === 'related' || line.change === 'unrelated';
}

/** Check if all lines have the same change type. */
function allChangesSame(lines: readonly RecordChangeLine[]): boolean {
  if (lines.length === 0) return true;
  const firstChange = lines[0]!.change;
  return lines.every((line) => line.change === firstChange);
}

/** The heading of a card that came from someone else's change: "Maya added What happens to the lake cottage". */
function authoredCardHeading(name: string, lines: readonly RecordChangeLine[]): string {
  if (lines.length > 0 && lines.every((line) => line.item === 'issue')) {
    return issueCardHeading(lines);
  }
  const topics = lines.filter((line) => line.item === 'topic').length;
  const people = lines.filter(
    (line) => line.item === 'person' && !isRelationshipLine(line),
  ).length;
  const first = name.split(' ')[0] ?? name;
  if (allChangesSame(lines) && lines[0]?.change === 'same_say') {
    return `${first} added ${lines.length === 1 ? lines[0]!.name : `${lines.length} people`} as a principal`;
  }
  if (
    allChangesSame(lines) &&
    lines[0]?.change === 'added' &&
    (topics === 0 || people === 0)
  ) {
    if (topics > 0) {
      return topics === 1
        ? `${first} added ${lines[0]!.name}`
        : `${first} added to the list`;
    }
    return `${first} added ${people === 1 ? 'a person' : `${people} people`}`;
  }
  return `${first} changed the list`;
}

/** Heading for record card: describes the kind of change when homogeneous, else "Updated in the list". */
function cardHeading(lines: readonly RecordChangeLine[]): string {
  if (lines.length > 0 && lines.every(isAddedIssueLine)) {
    return 'Added to your issues';
  }
  // A card about one issue is headed by the issue.
  if (lines.length > 0 && lines.every((line) => line.item === 'issue')) {
    return issueCardHeading(lines);
  }
  const same = allChangesSame(lines);
  const firstChange = lines.length > 0 ? lines[0]!.change : 'unknown';
  const people = lines.filter(
    (line) => line.item === 'person' && !isRelationshipLine(line),
  ).length;
  const count = people > 0 ? plural(people, 'person', 'people') : '';

  let heading: string;
  if (same) {
    switch (firstChange) {
      case 'added':
        heading = 'Added to the list';
        break;
      case 'removed':
        heading = 'Removed from the list';
        break;
      case 'renamed':
        heading = 'Renamed in the list';
        break;
      case 'address':
        heading = 'Updated in the list';
        break;
      case 'settled':
        heading = 'Settled';
        break;
      case 'reopened':
        heading = 'Reopened';
        break;
      case 'updated':
        heading = 'Updated topics';
        break;
      case 'same_say':
        heading = 'Made a principal';
        break;
      case 'assigned':
        heading = 'Topics assigned';
        break;
      case 'takes_part':
      case 'does_not_take_part':
        heading = 'Who takes part';
        break;
      case 'related':
      case 'unrelated':
        heading = 'Relationships';
        break;
      default:
        heading = 'Updated in the list';
    }
  } else {
    heading = 'Updated in the list';
  }
  return count === '' ? heading : `${heading} · ${count}`;
}

/** "Sam: Property and inheritance", "Sam: Organizing your documents and accounts", or the sentence form when `sentence` is true. */
function assignedText(line: RecordChangeLine, sentence = false): string {
  const labels = line.labels ?? [];
  const list =
    labels.length === 0
      ? 'no topics'
      : labels.length === 1
        ? labels[0]!
        : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]!}`;
  return sentence ? `assigned ${line.name} to ${list}` : `${line.name}: ${list}`;
}

/** A line Mor's commit wrote for an issue it put on a topic's list. */
function isAddedIssueLine(line: RecordChangeLine): boolean {
  return line.item === 'topic' && line.change === 'added' && line.decision !== undefined;
}

/** An added-issue line: the title, its topic, then Explore issue and Undo, or "Removed". */
function addedIssueLine(
  line: RecordChangeLine,
  added: NonNullable<MessageRowProps['added']>,
  planId: string,
  pathname: string,
): JSX.Element {
  const decisionId = line.decision ?? '';
  const decision = added.decisions.find((candidate) => candidate.id === decisionId);
  const topicKey = line.topic ?? decision?.topic ?? null;
  const topic = added.topics.find((candidate) => candidate.key === topicKey);
  const text = topic === undefined ? line.name : `${line.name} · ${topic.label}`;
  if (decision === undefined || topicKey === null) {
    return <>{`${text} · Removed`}</>;
  }
  if (!added.isPrincipal) {
    return <>{text}</>;
  }
  const thread = added.thread;
  const issueTitle =
    thread?.issue === null || thread?.issue === undefined
      ? null
      : (added.decisions.find((candidate) => candidate.id === thread.issue)?.label ??
        null);
  const threadTopic = added.topics.find((candidate) => candidate.key === thread?.topic);
  const backLabel =
    issueTitle !== null
      ? issueTitle
      : threadTopic === undefined
        ? null
        : topicPhrase(threadTopic);
  return (
    <>
      {text}
      <Link
        className="recordCard__undo"
        to={`/plans/${planId}/topics/${topicKey}/issues/${decisionId}`}
        {...(backLabel === null
          ? {}
          : { state: { back: { to: pathname, label: backLabel } } })}
      >
        Explore issue
      </Link>
      {!decision.opened ? (
        <button
          type="button"
          className="recordCard__undo"
          disabled={added.busy}
          onClick={() => added.remove(decisionId)}
        >
          Undo
        </button>
      ) : null}
    </>
  );
}

/** Format a line: bare or with prefix depending on whether card is homogeneous. */
function cardLine(line: RecordChangeLine, isMixed: boolean): string {
  if (line.item === 'issue') {
    return issuePhrase(line);
  }
  if (!isMixed) {
    // All lines have the same change type: bare lines (except renames and address)
    switch (line.change) {
      case 'renamed':
        return `${line.from ?? ''} → ${line.name}`;
      case 'address':
        return `${line.name}'s address`;
      case 'assigned':
        return assignedText(line);
      case 'takes_part':
        return `${line.name} takes part`;
      case 'does_not_take_part':
        return `${line.name} doesn’t take part`;
      case 'unrelated':
        return `Removed the relationship between ${line.name}`;
      default:
        return line.name;
    }
  } else {
    // Mixed types: add prefix (except renames and address which keep their format)
    switch (line.change) {
      case 'added':
        return `Added: ${line.name}`;
      case 'removed':
        return `Removed: ${line.name}`;
      case 'renamed':
        return `${line.from ?? ''} → ${line.name}`;
      case 'address':
        return `${line.name}'s address`;
      case 'settled':
        return `Settled: ${line.name}`;
      case 'reopened':
        return `Reopened: ${line.name}`;
      case 'updated':
        return `Updated: ${line.name}`;
      case 'same_say':
        return `${line.name} was asked to be a principal`;
      case 'assigned':
        return assignedText(line);
      case 'takes_part':
        return `${line.name} takes part`;
      case 'does_not_take_part':
        return `${line.name} doesn’t take part`;
      case 'unrelated':
        return `Removed the relationship between ${line.name}`;
      default:
        return line.name;
    }
  }
}

/** The issue's title, with "Corrected · " before it when every line is a correction. */
function issueCardHeading(lines: readonly RecordChangeLine[]): string {
  const title = lines[0]?.name ?? 'Updated in the list';
  return lines.every((line) => line.correction === true) ? `Corrected · ${title}` : title;
}

/** What an issue line says: "Corrected: " or "Updated: " first for a later change to an entry. */
function issuePhrase(line: RecordChangeLine): string {
  const text = issueText(line);
  return line.correction === undefined
    ? text
    : `${line.correction ? 'Corrected' : 'Updated'}: ${text}`;
}

function issueText(line: RecordChangeLine): string {
  const detail = line.detail ?? '';
  switch (line.change) {
    case 'want':
      return `What you want: ${detail}`;
    case 'why':
      return `Why it matters: ${detail}`;
    case 'next_step':
      return detail === ''
        ? 'Cleared the suggested next step'
        : `Suggested next step: ${detail}`;
    case 'arrangements':
      return detail === ''
        ? 'Cleared the existing arrangements'
        : `Existing arrangements: ${detail}`;
    case 'person_added':
    case 'person_updated':
      return `Who is involved · ${detail}`;
    case 'person_dismissed':
      return `Dismissed from who is involved: ${detail}`;
    case 'question_added':
      return `Added to what’s still open: ${detail}`;
    case 'question_updated':
      return `What’s still open · ${detail}`;
    case 'question_dismissed':
      return `Dismissed from what’s still open: ${detail}`;
    case 'check_added':
      return `Added to what needs checking: ${detail}`;
    case 'check_updated':
      return `What needs checking · ${detail}`;
    case 'check_dismissed':
      return `Dismissed from what needs checking: ${detail}`;
    case 'undone':
      return `Undid: ${detail}`;
    default:
      return line.name;
  }
}

/** Undo for one issue line, while this card or divider is still the latest word on its entry. */
function undoButton(
  line: RecordChangeLine,
  messageId: string,
  issue: MessageRowProps['issue'],
): JSX.Element | null {
  const entry = line.entry;
  if (
    issue === undefined ||
    entry === undefined ||
    line.item !== 'issue' ||
    line.change === 'undone' ||
    !(
      issue.turns.get(entryKey(entry.kind, entry.id)) === messageId ||
      (issue.newest === messageId && removesEntry(line))
    )
  ) {
    return null;
  }
  return (
    <button
      type="button"
      className="recordCard__undo"
      disabled={issue.busy}
      onClick={() => issue.undo(entry)}
    >
      Undo
    </button>
  );
}

/** A line that dismissed an entry or cleared a single field: the entry is not in the issue view. */
function removesEntry(line: RecordChangeLine): boolean {
  switch (line.change) {
    case 'person_dismissed':
    case 'question_dismissed':
    case 'check_dismissed':
      return true;
    case 'want':
    case 'why':
    case 'next_step':
    case 'arrangements':
      return line.detail === undefined || line.detail === '';
    default:
      return false;
  }
}

function editSentence(who: string, line: RecordChangeLine): string {
  if (line.item === 'issue') {
    return line.change === 'undone'
      ? issuePhrase(line)
      : `${who} changed the page: ${issuePhrase(line)}`;
  }
  const topic = line.item === 'topic';
  const quote = (text: string): string => `"${text}"`;
  switch (line.change) {
    case 'added':
      return `${who} added ${line.name}`;
    case 'renamed':
      return topic
        ? `${who} renamed ${quote(line.from ?? '')} to ${quote(line.name)}`
        : `${who} renamed ${line.from ?? ''} to ${line.name}`;
    case 'address':
      return `${who} changed ${line.name}'s address`;
    case 'removed':
      return `${who} removed ${line.name}`;
    case 'settled':
      return `${who} settled ${line.name}`;
    case 'updated':
      return `${who} updated ${line.name}`;
    case 'same_say':
      return `${who} added ${line.name} as a principal`;
    case 'invited':
      return `${who} invited ${line.name}`;
    case 'takes_part':
      return `${who} said ${line.name} takes part`;
    case 'does_not_take_part':
      return `${who} said ${line.name} doesn’t take part`;
    case 'assigned':
      return `${who} ${assignedText(line, true)}`;
    case 'related':
      return `${who}: ${line.name}`;
    case 'unrelated':
      return `${who} removed the relationship between ${line.name}`;
    default:
      return `${who} reopened ${line.name}`;
  }
}
